import { View, StyleSheet } from "react-native";
import WorkspaceSidebar from "../components/chat-companion/WorkspaceSidebar/WorkspaceSidebar";
import WorkspaceThreadsDrawer, {
  WorkspaceThread,
} from "../components/chat-companion/WorkspaceThreadsDrawer/WorkspaceThreadsDrawer";
import CreateThreadModal from "../components/chat-companion/CreateThreadModal/CreateThreadModal";
import {
  useGetAllCompanionChatsForUserQuery,
  useAddNewCompanionChatMutation,
  useDeleteCompanionChatMutation,
  GetAllCompanionChatsForUserDocument,
} from "../graphql/generated/graphql";
import { formatTimeAgo } from "../utils/date";
import { WorkspaceType } from "../components/chat-companion/WorkspaceCard/WorkspaceCard";
import { useWorkspaceThreads } from "../contexts/WorkspaceThreadsContext";
import { useState, useMemo, useRef, useEffect } from "react";
import { Animated } from "react-native";
import { useRoute, RouteProp, useNavigation, NavigationProp } from "@react-navigation/native";
import { RootStackParamList } from "../navigation/AppNavigator.web";

const DRAWER_WIDTH = 360;

function CompanionLayout({ children }) {
  const route = useRoute<RouteProp<RootStackParamList, "Companion">>();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { chatId: routeChatId } = route.params || {};
  const { data: chatsData, refetch: refetchChats } = useGetAllCompanionChatsForUserQuery({});
  const [addNewCompanionChat] = useAddNewCompanionChatMutation({
    refetchQueries: [{ query: GetAllCompanionChatsForUserDocument }],
  });
  const [deleteCompanionChat] = useDeleteCompanionChatMutation({
    refetchQueries: [{ query: GetAllCompanionChatsForUserDocument }],
  });
  const { openWorkspaceId, toggleWorkspaceDrawer, setActiveThreadId, activeThreadId, setOpenWorkspaceId } =
    useWorkspaceThreads();
  const [searchValue, setSearchValue] = useState("");
  const [isCreateThreadModalVisible, setCreateThreadModalVisible] = useState(false);
  const [customThreadName, setCustomThreadName] = useState("");
  const drawerWidthAnim = useRef(new Animated.Value(0)).current;

  const generalWorkspaces = [
    {
      id: "general_always_active",
      title: "The only general workspace for now",
      type: "general" as WorkspaceType,
      threadCount: chatsData?.getAllCompanionChatsForUser?.length ?? 0,
      lastActivityLabel: chatsData?.getAllCompanionChatsForUser?.[0]?.last_modified
        ? formatTimeAgo(chatsData.getAllCompanionChatsForUser[0].last_modified)
        : "",
      isActive: true,
    },
  ];

  const handleWorkspaceThreadsPress = (id: string) => {
    toggleWorkspaceDrawer(id);
  };

  const isDrawerOpen = openWorkspaceId !== null;

  useEffect(() => {
    Animated.timing(drawerWidthAnim, {
      toValue: isDrawerOpen ? DRAWER_WIDTH : 0,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [isDrawerOpen, drawerWidthAnim]);

  const workspaceName = generalWorkspaces.find((w) => w.id === openWorkspaceId)?.title || "";

  const threads: WorkspaceThread[] = useMemo(() => {
    if (!chatsData?.getAllCompanionChatsForUser) return [];

    return chatsData.getAllCompanionChatsForUser.map((chat) => ({
      id: chat.id,
      title: chat.name || "Untitled Thread",
      metaLabel: chat.last_modified ? formatTimeAgo(chat.last_modified) : "",
      type: "general" as const,
      isActive: activeThreadId === chat.id,
    }));
  }, [chatsData?.getAllCompanionChatsForUser, activeThreadId]);

  const filteredThreads = useMemo(() => {
    if (!searchValue.trim()) return threads;
    const lowerSearch = searchValue.toLowerCase();
    return threads.filter((thread) => thread.title.toLowerCase().includes(lowerSearch));
  }, [threads, searchValue]);

  const taskThreads: WorkspaceThread[] = [];
  const generalThreads: WorkspaceThread[] = filteredThreads;

  const handleSelectThread = (id: string) => {
    setActiveThreadId(id);
    setOpenWorkspaceId(null);
    navigation.setParams({ chatId: id });
  };

  const handleCreateThread = () => {
    setCreateThreadModalVisible(true);
  };

  const handleCloseCreateThreadModal = () => {
    setCreateThreadModalVisible(false);
    setCustomThreadName("");
  };

  const handleCreateCustomThread = async () => {
    try {
      const name = customThreadName.trim() || "New Thread";
      const result = await addNewCompanionChat({ variables: { name } });
      const newChatId = result.data?.addNewCompanionChat?.data?.id;
      if (newChatId) {
        setOpenWorkspaceId(null);
        setActiveThreadId(newChatId);
        navigation.setParams({ chatId: newChatId });
        await refetchChats();
      }
      handleCloseCreateThreadModal();
    } catch (error) {
      console.error("Error creating new companion chat:", error);
    }
  };

  const handleDeleteThread = async (id: string) => {
    try {
      const result = await deleteCompanionChat({ variables: { id } });
      if (result.data?.deleteCompanionChat?.success) {
        // If the deleted thread was the active one, navigate to the latest thread or clear the chat
        if (activeThreadId === id) {
          setActiveThreadId(null);
          navigation.setParams({ chatId: undefined });
        }
        await refetchChats();
      }
    } catch (error) {
      console.error("Error deleting companion chat:", error);
    }
  };

  useEffect(() => {
    if (!routeChatId) {
      return;
    }
    setActiveThreadId(routeChatId);
    void refetchChats();
  }, [routeChatId, setActiveThreadId, refetchChats]);

  return (
    <View style={[styles.container, { flexDirection: "column" }]}>
      <View style={[styles.content]}>
        <WorkspaceSidebar
          generalWorkspaces={generalWorkspaces}
          courseWorkspaces={[]}
          supportWorkspaces={[]}
          todoWorkspaces={[]}
          settingsWorkspaces={[]}
          activeWorkspaceId="general_always_active"
          onWorkspacePress={() => {}}
          onWorkspaceThreadsPress={handleWorkspaceThreadsPress}
        />
        <View style={styles.childrenWrapper}>{children}</View>
        <Animated.View style={[styles.drawerWrapper, { width: drawerWidthAnim }]}>
          <WorkspaceThreadsDrawer
            visible={isDrawerOpen}
            workspaceName={workspaceName}
            onClose={() => setOpenWorkspaceId(null)}
            searchValue={searchValue}
            onChangeSearch={setSearchValue}
            onCreateThread={handleCreateThread}
            taskThreads={taskThreads}
            generalThreads={generalThreads}
            onSelectThread={handleSelectThread}
            onDeleteThread={handleDeleteThread}
          />
        </Animated.View>
      </View>
      <CreateThreadModal
        visible={isCreateThreadModalVisible}
        onRequestClose={handleCloseCreateThreadModal}
        customThreadName={customThreadName}
        onChangeCustomThreadName={setCustomThreadName}
        onCreateCustomThread={handleCreateCustomThread}
        taskActivities={[]}
        assignmentGroups={[]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
    // @ts-expect-error - works on web
    maxHeight: "100vh",
    overflow: "hidden",
  },
  content: {
    flex: 1,
    flexDirection: "row",
  },
  drawerWrapper: {
    position: "absolute",
    left: 272, // Width of WorkspaceSidebar
    top: 0,
    bottom: 0,
    overflow: "hidden",
    zIndex: 10,
  },
  childrenWrapper: {
    flex: 1,
  },
});

export default CompanionLayout;
